import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader2, AlertCircle, Check, ArrowUp, ArrowDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { formatPrice as fmtNum } from '@/lib/format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Field config — declarative so adding a column later is a one-line change.
const COLUMNS = [
  { key: 'product_name',           label: 'Name',        type: 'text',   width: 'w-[200px]', editable: true,  sticky: true },
  { key: 'product_brand',          label: 'Brand',       type: 'text',   width: 'w-[140px]', editable: true },
  { key: 'product_size',           label: 'Size',        type: 'text',   width: 'w-[100px]', editable: true },
  { key: 'product_color',          label: 'Color',       type: 'text',   width: 'w-[100px]', editable: true },
  { key: 'vendor_id',              label: 'Vendor',      type: 'vendor', width: 'w-[160px]', editable: true },
  { key: 'category',               label: 'Category',    type: 'text',   width: 'w-[120px]', editable: true },
  { key: 'unit',                   label: 'Unit',        type: 'text',   width: 'w-[80px]',  editable: true },
  { key: 'mrp',                    label: 'MRP',         type: 'number', width: 'w-[110px]', editable: true,  align: 'right' },
  { key: 'vendor_discount_percent', label: 'V.Disc %',   type: 'number', width: 'w-[90px]',  editable: true,  align: 'right' },
  { key: 'gst_percent',            label: 'GST %',       type: 'number', width: 'w-[90px]',  editable: true,  align: 'right' },
  { key: 'margin_percent',         label: 'Margin %',    type: 'number', width: 'w-[100px]', editable: true,  align: 'right' },
  { key: 'input_price',            label: 'Input ₹',     type: 'number', width: 'w-[110px]', editable: true,  align: 'right' },
  { key: 'selling_price',          label: 'Selling ₹',   type: 'number', width: 'w-[110px]', editable: false, align: 'right' }, // derived
  { key: 'customer_discount_percent', label: 'Cust.Disc %', type: 'number', width: 'w-[100px]', editable: false, align: 'right' },
  { key: 'profit_margin',          label: 'Profit ₹',    type: 'number', width: 'w-[110px]', editable: false, align: 'right' },
  { key: 'is_active',              label: 'Active',      type: 'bool',   width: 'w-[80px]',  editable: true,  align: 'center' },
];

// One editable cell. Inputs go inline; save on blur/Enter; revert on Escape.
function EditableCell({ value, type, vendors, onSave, align }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (!editing) setDraft(value ?? ''); }, [value, editing]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = async () => {
    const newVal = type === 'number' ? (draft === '' ? 0 : Number(draft)) : draft;
    if (newVal === value) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(newVal);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 800);
    } catch {
      setDraft(value ?? ''); // revert on error
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    else if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
  };

  if (type === 'bool') {
    return (
      <div className="flex items-center justify-center h-full">
        <input
          type="checkbox"
          checked={!!value}
          onChange={async (e) => {
            setSaving(true);
            try {
              await onSave(e.target.checked);
              setSavedFlash(true); setTimeout(() => setSavedFlash(false), 800);
            } finally { setSaving(false); }
          }}
          className="w-3.5 h-3.5 accent-amber-700 cursor-pointer disabled:opacity-50"
          disabled={saving}
        />
        {saving && <Loader2 className="w-3 h-3 ml-1 text-amber-600 animate-spin" />}
      </div>
    );
  }

  if (!editing) {
    const display = type === 'vendor'
      ? (vendors.find(v => v.vendor_id === value)?.vendor_name || '—')
      : (type === 'number' ? fmtNum(value) : (value || ''));
    return (
      <div
        onClick={() => setEditing(true)}
        className={`w-full h-full px-2 py-1.5 cursor-text hover:bg-amber-50/60 truncate text-${align || 'left'} ${savedFlash ? 'bg-lime-50 transition-colors' : ''}`}
        title="Click to edit"
      >
        {saving ? <Loader2 className="w-3 h-3 inline animate-spin text-amber-600" /> : (display || <span className="text-stone-300">—</span>)}
      </div>
    );
  }

  if (type === 'vendor') {
    return (
      <Select
        value={draft || ''}
        onValueChange={async (v) => {
          setDraft(v);
          // Save immediately on selection (no separate blur)
          if (v !== value) {
            setSaving(true);
            try { await onSave(v); setSavedFlash(true); setTimeout(() => setSavedFlash(false), 800); }
            catch { setDraft(value ?? ''); }
            finally { setSaving(false); setEditing(false); }
          } else setEditing(false);
        }}
        open
        onOpenChange={(o) => { if (!o) setEditing(false); }}
      >
        <SelectTrigger className="h-7 text-xs border-amber-300 w-full"><SelectValue /></SelectTrigger>
        <SelectContent>
          {vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      ref={inputRef}
      type={type === 'number' ? 'number' : 'text'}
      step="any"
      value={draft}
      onChange={(e) => {
        const v = e.target.value;
        // Cap money/percent inputs at 2 decimals while typing.
        if (type === 'number' && v !== '' && !/^\d*\.?\d{0,2}$/.test(v)) return;
        setDraft(v);
      }}
      onBlur={commit}
      onKeyDown={onKey}
      className={`h-7 px-2 py-0.5 text-xs border-amber-300 ring-1 ring-amber-200 bg-white text-${align || 'left'}`}
    />
  );
}

export default function ProductsGrid({ products, vendors, onProductUpdated }) {
  const [sortBy, setSortBy] = useState('product_name');
  const [sortDir, setSortDir] = useState('asc');
  const [filter, setFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('ALL');
  const [activeFilter, setActiveFilter] = useState('ALL');

  const handleSort = (key) => {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
  };

  const filteredSorted = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let rows = products.filter(p => {
      if (vendorFilter !== 'ALL' && p.vendor_id !== vendorFilter) return false;
      if (activeFilter === 'ACTIVE' && p.is_active === false) return false;
      if (activeFilter === 'INACTIVE' && p.is_active !== false) return false;
      if (!q) return true;
      const v = vendors.find(x => x.vendor_id === p.vendor_id);
      const hay = `${p.product_name} ${p.product_brand} ${p.product_size} ${p.product_color} ${p.category} ${v?.vendor_name || ''}`.toLowerCase();
      return hay.includes(q);
    });
    rows.sort((a, b) => {
      const A = a[sortBy] ?? '';
      const B = b[sortBy] ?? '';
      if (typeof A === 'number' && typeof B === 'number') return sortDir === 'asc' ? A - B : B - A;
      return sortDir === 'asc' ? String(A).localeCompare(String(B)) : String(B).localeCompare(String(A));
    });
    return rows;
  }, [products, vendors, filter, vendorFilter, activeFilter, sortBy, sortDir]);

  const saveCell = useCallback(async (product_id, key, newValue) => {
    try {
      const res = await axios.patch(`${BACKEND_URL}/api/products/${product_id}`, { [key]: newValue }, { withCredentials: true });
      onProductUpdated(res.data);
      toast.success(`Saved`, { duration: 1200 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
      throw err; // so EditableCell reverts
    }
  }, [onProductUpdated]);

  return (
    <div className="space-y-3">
      <div className="bg-white border border-stone-200 rounded-md p-2 flex flex-wrap gap-2 items-center">
        <Input
          data-testid="grid-search"
          placeholder="Search across all columns…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="text-xs h-8 border-stone-200 flex-1 min-w-[200px]"
        />
        <Select value={vendorFilter} onValueChange={setVendorFilter}>
          <SelectTrigger data-testid="grid-vendor-filter" className="h-8 text-xs border-stone-200 w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All vendors</SelectItem>
            {vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger data-testid="grid-active-filter" className="h-8 text-xs border-stone-200 w-[120px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="INACTIVE">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-[10px] text-stone-500 px-2 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          <span>Click a cell to edit. Press <kbd className="px-1 bg-stone-100 border border-stone-200 rounded text-[9px]">Enter</kbd> to save, <kbd className="px-1 bg-stone-100 border border-stone-200 rounded text-[9px]">Esc</kbd> to cancel.</span>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-md overflow-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
        <table className="text-xs border-collapse min-w-full">
          <thead className="bg-stone-100 sticky top-0 z-10">
            <tr>
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  data-testid={`grid-col-${col.key}`}
                  onClick={() => handleSort(col.key)}
                  className={`${col.width} text-${col.align || 'left'} px-2 py-2 text-[10px] uppercase tracking-wide font-bold text-stone-700 border-b border-r border-stone-200 cursor-pointer hover:bg-stone-200 select-none ${col.sticky ? 'sticky left-0 bg-stone-100 z-20' : ''}`}
                >
                  <div className="flex items-center gap-1 justify-between">
                    <span>{col.label}{!col.editable && <span className="text-stone-400 ml-0.5" title="Computed">∗</span>}</span>
                    {sortBy === col.key && (sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredSorted.length === 0 ? (
              <tr><td colSpan={COLUMNS.length} className="px-4 py-8 text-center text-stone-500 text-xs">No products match the filters.</td></tr>
            ) : filteredSorted.map(p => (
              <tr key={p.product_id} data-testid={`grid-row-${p.product_id}`} className="border-b border-stone-100 hover:bg-stone-50/60">
                {COLUMNS.map(col => (
                  <td key={col.key}
                      className={`${col.width} border-r border-stone-100 align-middle ${col.sticky ? 'sticky left-0 bg-white' : ''}`}
                      data-testid={`grid-cell-${p.product_id}-${col.key}`}
                  >
                    {col.editable ? (
                      <EditableCell
                        value={p[col.key]}
                        type={col.type}
                        align={col.align}
                        vendors={vendors}
                        onSave={(v) => saveCell(p.product_id, col.key, v)}
                      />
                    ) : (
                      <div className={`px-2 py-1.5 text-${col.align || 'left'} truncate text-stone-500 italic`}>
                        {col.type === 'number' ? fmtNum(p[col.key]) : (p[col.key] || '—')}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-stone-400 italic px-1">
        Showing {filteredSorted.length} of {products.length} products. Columns marked ∗ are computed from other fields (Selling, Cust.Disc, Profit, etc.).
      </div>
    </div>
  );
}
